import { Button, FormLabel, IconButton, Stack, Textarea, Tooltip, Typography } from "@mui/joy";
import { Info } from "@phosphor-icons/react";
import { Session } from "@supabase/supabase-js";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { supabase } from "../supabaseClient";

interface ReflectionProps {
  hide?: boolean;
  problemName: string;
  question: string;
}

export default function ReflectionInput({ hide, problemName, question }: ReflectionProps) {
  const [text, setText] = useState("");
  const [showTooltip, setShowTooltip] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const { session } = useOutletContext<{ session: Session | null }>();

  if (hide || !session) return <></>

  async function handleSubmission() {
    setLoading(true);
    setError("");
    setSuccess("");

    const { user } = session!!;

    const reflection = {
      question: question,
      answer: text
    }

    // get all submissions for this user on this problem
    const { data: fetchData, error: fetchError } = await supabase
      .from("submissions")
      .select()
      .eq('profile_id', user.id)
      .eq('problem_title', problemName)
      .order('submitted_at', { ascending: false });

    if(!fetchData || fetchError){
      setError("Error submitting reflection.");
      return;
    }

    // gets the submission(s) without reflections
    const noReflSubs = fetchData.filter((sub) => {
      return sub["reflection"] === null;
    });

    var subToUpdateID;

    if(noReflSubs.length > 0){
      subToUpdateID = noReflSubs[0]["submission_id"];
    }else{
      // this means there was no new submission, 
      // likely a second reflection "Submit" press
      // update the last submission's reflection instead
      subToUpdateID = fetchData[0]["submission_id"]
    }

    const { error: updateError } = await supabase
      .from("submissions")
      .update({ "reflection": reflection })
      .eq('submission_id', subToUpdateID);

    setLoading(false);

    if(updateError){
      setError("Error submitting reflection.");
    }else{
      setSuccess("Successfully submitted reflection!");
    }
  }

  return (
    <Stack direction="column" width="100%" height="100%" gap={1}>
      <Stack direction="row" gap={1} alignItems="center">
        <Typography level="title-lg">Reflection Prompt</Typography>
        <Tooltip
          sx={{ maxWidth: "15em" }} 
          title="This reflection will be associated with the most recent successful submission. Make sure to submit a reflection before submitting code again." 
          placement="right" 
          arrow
          open={showTooltip}
          onOpen={() => setShowTooltip(true)}
          onClose={() => setShowTooltip(false)}
        >  
          <IconButton onClick={() => setShowTooltip(!showTooltip)}>
            <Info size={24} />
          </IconButton>
        </Tooltip>
      </Stack>
      <FormLabel>{question}</FormLabel>
      <Textarea
        sx={{ width: "100%", height: "100%", border: 2, borderRadius: 10 }}
        placeholder="Enter your reflection..."
        minRows={5}
        maxRows={10}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="backgroundAnimated"
        endDecorator={
          <Stack
            width="100%" 
            direction="row" 
            alignItems="center" 
            justifyContent="space-between" 
            borderTop="1px solid" 
            pt="var(--Textarea-paddingBlock)"
          >
            <Typography level="body-xs">
              {
                text.trim() === "" ? "0 word(s)" :
                `${text.trim().split(" ").length } word(s)`
              }
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              {
                !!success && <Typography level="body-sm" color="success">{success}</Typography>
              }
              {
                !!error && <Typography level="body-sm" color="danger">{error}</Typography>
              }
              <Button disabled={!text.trim() || loading} onClick={async () => await handleSubmission()}>Submit</Button>
            </Stack>
          </Stack>
        }
      />
    </Stack>
  )
}
